
import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, PlusCircle, CheckCircle2, Video, Clock } from 'lucide-react';
import { Job, JobStatus } from '../types';

interface JobCalendarProps {
  jobs: Job[];
  interviews?: Array<{ id: string, title: string, date: string }>;
  onDayClick?: (date: Date) => void;
  onJobClick?: (job: Job) => void;
}

export const JobCalendar: React.FC<JobCalendarProps> = ({ jobs, interviews = [], onDayClick, onJobClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getJobsForDay = (date: Date) => {
    return jobs.filter(job => isSameDay(new Date(job.date), date));
  };

  const getInterviewsForDay = (date: Date) => {
    return interviews.filter(inv => isSameDay(new Date(inv.date), date));
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  return (
    <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-8 flex items-center justify-between border-b border-slate-100 bg-slate-50/50">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <div className="flex gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Confirmed Jobs</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Interviews</span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <button onClick={prevMonth} className="p-3 bg-white hover:bg-slate-100 rounded-2xl transition-all border border-slate-200 shadow-sm">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <button onClick={nextMonth} className="p-3 bg-white hover:bg-slate-100 rounded-2xl transition-all border border-slate-200 shadow-sm">
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-slate-100 bg-white">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-slate-100">
        {days.map((day, idx) => {
          const dayJobs = getJobsForDay(day);
          const dayInterviews = getInterviewsForDay(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <div 
              key={idx} 
              onClick={() => onDayClick?.(day)}
              className={`bg-white min-h-[140px] p-3 hover:bg-slate-50 cursor-pointer transition-colors group relative ${!isSameMonth(day, currentMonth) ? 'opacity-30' : ''}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className={`text-sm font-black transition-all ${isToday ? 'bg-emerald-600 text-white w-8 h-8 flex items-center justify-center rounded-2xl shadow-lg shadow-emerald-100' : 'text-slate-700'}`}>
                  {format(day, 'd')}
                </span>
                <PlusCircle className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              
              <div className="space-y-1.5">
                {dayJobs.map(job => (
                  <div 
                    key={job.id} 
                    onClick={(e) => {
                      e.stopPropagation();
                      onJobClick?.(job);
                    }}
                    className={`text-[10px] p-2 rounded-xl border flex items-center gap-1.5 font-bold transition-all hover:scale-[1.02] ${
                      job.status === JobStatus.FILLED 
                        ? 'bg-emerald-600 text-white border-emerald-500 shadow-sm shadow-emerald-100' 
                        : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}
                  >
                    {job.status === JobStatus.FILLED ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" /> : <Clock className="w-3 h-3 flex-shrink-0" />}
                    <span className="truncate">{job.title}</span>
                  </div>
                ))}
                
                {dayInterviews.map(inv => (
                  <div 
                    key={inv.id}
                    className="text-[10px] p-2 bg-blue-50 text-blue-700 border border-blue-100 rounded-xl flex items-center gap-1.5 font-bold transition-all hover:scale-[1.02]"
                  >
                    <Video className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{inv.title}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
